import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config';
import { defaultTone } from './trainAgentConstants';

export function useTrainAgentState() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [role, setRole] = useState('');
  const [constraintsList, setConstraintsList] = useState([{ text: '', critical: false }]);
  const [tone, setTone] = useState(defaultTone());
  const [companyInfo, setCompanyInfo] = useState('');
  const [companyInfoFiles, setCompanyInfoFiles] = useState([]);
  const [companyInfoUrls, setCompanyInfoUrls] = useState([]);
  const [ctaButtons, setCtaButtons] = useState([]);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [examples, setExamples] = useState([{ input: '', output: '' }]);
  const [argumentsList, setArgumentsList] = useState([{ objection: '', response: '' }]);

  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/clients`)
      .then((res) => setClients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMessage({ type: 'error', text: 'Erro ao carregar clientes.' }));
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setBots([]);
      setSelectedBotId('');
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE}/api/bots`, { params: { clientId: selectedClientId } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setBots(list);
        setSelectedBotId(list[0] ? list[0].id : '');
      })
      .catch(() => {
        setBots([]);
        setMessage({ type: 'error', text: 'Erro ao carregar bots.' });
      })
      .finally(() => setLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedBotId) {
      setBot(null);
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    axios
      .get(`${API_BASE}/api/bots/${selectedBotId}`)
      .then((res) => {
        const b = res.data;
        setBot(b);
        const tc = b.trainingConfig || {};
        setRole(tc.role ?? '');
        if (Array.isArray(tc.constraintsList) && tc.constraintsList.length > 0) {
          setConstraintsList(
            tc.constraintsList.map((c) => ({ text: c.text || '', critical: Boolean(c.critical) }))
          );
        } else {
          const criticalSet = new Set((tc.criticalConstraints || []).map((s) => s.trim()).filter(Boolean));
          const lines = (tc.constraints || '')
            .split(/\r?\n/)
            .map((l) => l.replace(/^\s*[-*]\s*/, '').trim())
            .filter(Boolean);
          setConstraintsList(
            lines.length > 0
              ? lines.map((text) => ({ text, critical: criticalSet.has(text) }))
              : [{ text: '', critical: false }]
          );
        }
        setTone({
          estilo: tc.tone?.estilo ?? '',
          tom: tc.tone?.tom ?? '',
          emojis: tc.tone?.emojis ?? '',
          temperatura: typeof tc.tone?.temperatura === 'number' ? tc.tone.temperatura : 0.7,
          tipoLinguagem: tc.tone?.tipoLinguagem ?? ''
        });
        setCompanyInfo(tc.companyInfo ?? '');
        setCompanyInfoFiles(Array.isArray(tc.companyInfoFiles) ? tc.companyInfoFiles : []);
        setCompanyInfoUrls(Array.isArray(tc.companyInfoUrls) ? tc.companyInfoUrls.filter(Boolean) : []);
        setCtaButtons(
          Array.isArray(tc.ctaButtons) && tc.ctaButtons.length
            ? tc.ctaButtons.map((btn) => ({
                link: btn.link || '',
                label: btn.label || '',
                backgroundColor: btn.backgroundColor || '#0066FF',
                textColor: btn.textColor || '#FFFFFF',
                size: btn.size || 'medio',
                conversionEvent: btn.conversionEvent === 'schedule' ? 'none' : btn.conversionEvent || 'none',
                value: btn.value != null ? btn.value : '',
                currency: btn.currency || 'BRL',
                contentId: btn.contentId || '',
                contentType: btn.contentType || 'product'
              }))
            : []
        );
        setMediaLibrary(
          Array.isArray(tc.mediaLibrary) && tc.mediaLibrary.length
            ? tc.mediaLibrary.map((m) => ({
                type: m.type || 'link',
                url: m.url || '',
                label: m.label || '',
                description: m.description || ''
              }))
            : []
        );
        setExamples(
          Array.isArray(tc.examples) && tc.examples.length
            ? tc.examples.map((e) => ({ input: e.input || '', output: e.output || '' }))
            : [{ input: '', output: '' }]
        );
        setArgumentsList(
          Array.isArray(tc.arguments) && tc.arguments.length
            ? tc.arguments.map((a) => ({ objection: a.objection || '', response: a.response || '' }))
            : [{ objection: '', response: '' }]
        );
      })
      .catch(() => setMessage({ type: 'error', text: 'Erro ao carregar bot.' }))
      .finally(() => setLoading(false));
  }, [selectedBotId]);

  const handleSave = async () => {
    if (!selectedBotId) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    const trainingConfig = {
      role,
      constraintsList:
        constraintsList.filter((c) => c.text.trim()).length > 0
          ? constraintsList.filter((c) => c.text.trim()).map((c) => ({ text: c.text.trim(), critical: c.critical }))
          : [{ text: '', critical: false }],
      tone: { ...tone },
      companyInfo,
      companyInfoUrls: companyInfoUrls.filter((u) => u && String(u).trim()),
      ctaButtons: ctaButtons
        .filter((b) => b.link || b.label)
        .map((b) => ({
          link: b.link || '#',
          label: b.label || 'Botão',
          backgroundColor: b.backgroundColor || '#0066FF',
          textColor: b.textColor || '#FFFFFF',
          size: b.size || 'medio',
          conversionEvent: ['none', 'add_to_cart', 'lead'].includes(b.conversionEvent) ? b.conversionEvent : 'none',
          value: b.value === '' || b.value == null ? null : Number(b.value),
          currency: b.currency || 'BRL',
          contentId: b.contentId || '',
          contentType: b.contentType || 'product'
        })),
      mediaLibrary: mediaLibrary
        .filter((m) => m.url?.trim())
        .map((m) => ({
          type: ['image', 'gif', 'pdf', 'link'].includes(m.type) ? m.type : 'link',
          url: m.url.trim(),
          label: m.label || '',
          description: m.description || ''
        })),
      examples: examples.filter((e) => e.input.trim() || e.output.trim()),
      arguments: argumentsList.filter((a) => a.objection.trim() || a.response.trim())
    };
    if (trainingConfig.examples.length === 0) trainingConfig.examples = [{ input: '', output: '' }];
    if (trainingConfig.arguments.length === 0) trainingConfig.arguments = [{ objection: '', response: '' }];
    try {
      await axios.put(`${API_BASE}/api/bots/${selectedBotId}`, { trainingConfig });
      setMessage({ type: 'success', text: 'Treino salvo com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar.' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedBotId || !uploadFile) {
      setMessage({ type: 'error', text: 'Selecione um arquivo (.txt, .docx ou .pdf).' });
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`${API_BASE}/api/bots/${selectedBotId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadFile(null);
      const res = await axios.get(`${API_BASE}/api/bots/${selectedBotId}`);
      const tc = res.data.trainingConfig || {};
      setCompanyInfoFiles(Array.isArray(tc.companyInfoFiles) ? tc.companyInfoFiles : []);
      setMessage({ type: 'success', text: 'Arquivo enviado com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro no upload.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async (index) => {
    if (!selectedBotId) return;
    setMessage({ type: '', text: '' });
    try {
      await axios.delete(`${API_BASE}/api/bots/${selectedBotId}/company-info-files/${index}`);
      setCompanyInfoFiles((prev) => prev.filter((_, i) => i !== index));
      setMessage({ type: 'success', text: 'Arquivo removido.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao remover arquivo.' });
    }
  };

  const handleTierChange = async (newTier) => {
    if (!selectedBotId || newTier === (bot?.tier || 'avancado')) return;
    try {
      await axios.put(`${API_BASE}/api/bots/${selectedBotId}`, { tier: newTier });
      setBot((prev) => ({ ...prev, tier: newTier }));
      setMessage({ type: 'success', text: 'Modelo atualizado.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao atualizar modelo.' });
    }
  };

  const addExample = () => setExamples((prev) => [...prev, { input: '', output: '' }]);
  const removeExample = (i) => setExamples((prev) => prev.filter((_, idx) => idx !== i));
  const setExample = (i, field, value) =>
    setExamples((prev) => prev.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));

  const addArgument = () => setArgumentsList((prev) => [...prev, { objection: '', response: '' }]);
  const removeArgument = (i) => setArgumentsList((prev) => prev.filter((_, idx) => idx !== i));
  const setArgument = (i, field, value) =>
    setArgumentsList((prev) => prev.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)));

  const addConstraint = () => setConstraintsList((prev) => [...prev, { text: '', critical: false }]);
  const removeConstraint = (i) =>
    setConstraintsList((prev) =>
      prev.length <= 1 ? [{ text: '', critical: false }] : prev.filter((_, idx) => idx !== i)
    );
  const setConstraint = (i, field, value) =>
    setConstraintsList((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  const moveConstraint = (i, direction) => {
    if (direction === 'up' && i <= 0) return;
    if (direction === 'down' && i >= constraintsList.length - 1) return;
    const j = direction === 'up' ? i - 1 : i + 1;
    setConstraintsList((prev) => {
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const addCompanyUrl = () => setCompanyInfoUrls((prev) => [...prev, '']);
  const removeCompanyUrl = (i) => setCompanyInfoUrls((prev) => prev.filter((_, idx) => idx !== i));
  const setCompanyUrl = (i, value) =>
    setCompanyInfoUrls((prev) => prev.map((u, idx) => (idx === i ? value : u)));

  const addCtaButton = () =>
    setCtaButtons((prev) => [
      ...prev,
      {
        link: '',
        label: '',
        backgroundColor: '#0066FF',
        textColor: '#FFFFFF',
        size: 'medio',
        conversionEvent: 'none',
        value: '',
        currency: 'BRL',
        contentId: '',
        contentType: 'product'
      }
    ]);
  const removeCtaButton = (i) => setCtaButtons((prev) => prev.filter((_, idx) => idx !== i));
  const setCtaButton = (i, field, value) =>
    setCtaButtons((prev) => prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)));

  const addMediaItem = () =>
    setMediaLibrary((prev) => [...prev, { type: 'link', url: '', label: '', description: '' }]);
  const removeMediaItem = (i) => setMediaLibrary((prev) => prev.filter((_, idx) => idx !== i));
  const setMediaItem = (i, field, value) =>
    setMediaLibrary((prev) => prev.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));

  return {
    clients,
    selectedClientId,
    setSelectedClientId,
    bots,
    selectedBotId,
    setSelectedBotId,
    bot,
    loading,
    saving,
    uploading,
    message,
    role,
    setRole,
    constraintsList,
    tone,
    setTone,
    companyInfo,
    setCompanyInfo,
    companyInfoFiles,
    companyInfoUrls,
    ctaButtons,
    mediaLibrary,
    examples,
    argumentsList,
    uploadFile,
    setUploadFile,
    handleSave,
    handleUpload,
    handleRemoveFile,
    handleTierChange,
    addExample,
    removeExample,
    setExample,
    addArgument,
    removeArgument,
    setArgument,
    addConstraint,
    removeConstraint,
    setConstraint,
    moveConstraint,
    addCompanyUrl,
    removeCompanyUrl,
    setCompanyUrl,
    addCtaButton,
    removeCtaButton,
    setCtaButton,
    addMediaItem,
    removeMediaItem,
    setMediaItem
  };
}
